import { AxiosError, InternalAxiosRequestConfig } from "axios";

import api from "@/api";
import { logger } from "@/sentry";

// exclude anonymous user creation endpoint as it causes infinite loop
// and any unauthenticated endpoints
const EXCLUDED_PATHS = ["/user/anonymous", "/suggestions/landing"];
interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  config: InternalAxiosRequestConfig;
}
class ApiInterceptorManager {
  private isRefreshingAnonymousToken = false;
  private requestQueue: QueuedRequest[] = [];

  constructor(
    private getAccessToken: () => Promise<string | null>,
    private refreshAnonymousToken: () => Promise<void>,
    private logout: () => Promise<void>,
    private createAnonymousSession: () => Promise<void>,
  ) {
    this.setupInterceptors();
  }

  private processQueue(error: any = null) {
    this.requestQueue.forEach((request) => {
      if (error) {
        request.reject(error);
      } else {
        request.resolve(request.config);
      }
    });

    this.requestQueue = [];
  }

  private setupInterceptors() {
    api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      const shouldExclude = EXCLUDED_PATHS.some((endpoint) =>
        config.url?.includes(endpoint),
      );

      if (shouldExclude) {
        return config;
      }

      const accessToken = await this.getAccessToken();

      if (accessToken && accessToken.length > 0) {
        config.headers.set("Authorization", `Bearer ${accessToken}`);
      }
      // TODO: Add USER_AGENT and other relevant headers
      return config;
    });

    // Configure response interceptor to try to use refresh tokens if
    // a request fails with a 401 exception
    api.interceptors.response.use(null, async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };
      const status = error?.response?.status;

      // Don't retry excluded paths - they handle their own auth
      const shouldExclude = EXCLUDED_PATHS.some((endpoint) =>
        originalRequest?.url?.includes(endpoint),
      );
      if (status === 401 && !originalRequest?._retry && !shouldExclude) {
        // mark this request as retried
        originalRequest._retry = true;
        // if we are already refreshing, queue this request to be retried after the refresh is complete
        if (this.isRefreshingAnonymousToken) {
          return new Promise((resolve, reject) => {
            this.requestQueue.push({
              resolve: (config) => {
                resolve(api(config));
              },
              reject,
              config: originalRequest,
            });
          });
        }
        try {
          logger.info("Refreshing anonymous token, will retry request(s)", {
            category: "auth",
          });
          this.isRefreshingAnonymousToken = true;
          await this.refreshAnonymousToken();
          // refresh succeeded, process queue with out error
          this.processQueue(null);
          return api(originalRequest);
        } catch (_refreshError) {
          logger.info("Refresh failed, attempting new anonymous session", {
            category: "auth",
          });
          try {
            await this.createAnonymousSession();
            this.processQueue(null);
            return api(originalRequest);
          } catch (createError) {
            logger.error("Failed to create new anonymous session", {
              category: "auth",
              error: createError,
            });
            this.processQueue(createError);
            await this.logout();
            return Promise.reject(createError);
          }
        } finally {
          this.isRefreshingAnonymousToken = false;
        }
      }
      // For non-401 errors or if retry already attempted, just reject
      return Promise.reject(error);
    });
  }
}

export const configureAxiosInterceptor = (
  getAccessToken: () => Promise<string | null>,
  refreshAnonymousToken: () => Promise<void>,
  logout: () => Promise<void>,
  createAnonymousSession: () => Promise<void>,
) => {
  return new ApiInterceptorManager(
    getAccessToken,
    refreshAnonymousToken,
    logout,
    createAnonymousSession,
  );
};
